import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { formatCurrency, formatPoints } from '../../utils/formatters';
import type { BuydownScenario } from '../utils/buydownCalculations';

interface ComparisonCardProps {
  pinnedScenario: {
    loanType: string;
    occupancy: string;
    label: string;
    basePoints: number;
    investorPoints: number;
    scenarios: {
      fixed?: BuydownScenario;
      oneZero?: BuydownScenario;
      twoOne?: BuydownScenario;
      threeTwoOne?: BuydownScenario;
    };
  };
  currentScenario: {
    loanType: string;
    occupancy: string;
    label: string;
    basePoints: number;
    investorPoints: number;
    scenarios: {
      fixed?: BuydownScenario;
      oneZero?: BuydownScenario;
      twoOne?: BuydownScenario;
      threeTwoOne?: BuydownScenario;
    };
  };
  onClearComparison: () => void;
}

export const ComparisonCard: React.FC<ComparisonCardProps> = ({
  pinnedScenario,
  currentScenario,
  onClearComparison
}) => {
  const compareValues = (pinned: number, current: number) => {
    const diff = current - pinned;
    const percentDiff = pinned !== 0 ? (diff / pinned) * 100 : 0;
    
    if (Math.abs(diff) < 0.01) {
      return { icon: Minus, color: 'text-muted-foreground', text: 'Same', value: 0 };
    }
    
    // For costs, lower is better
    if (diff < 0) {
      return { 
        icon: TrendingDown, 
        color: 'text-green-600 dark:text-green-400', 
        text: `${Math.abs(percentDiff).toFixed(1)}% lower`,
        value: diff
      };
    } else {
      return { 
        icon: TrendingUp, 
        color: 'text-red-600 dark:text-red-400', 
        text: `${percentDiff.toFixed(1)}% higher`,
        value: diff
      };
    }
  };

  const pinnedFixed = pinnedScenario.scenarios.fixed;
  const currentFixed = currentScenario.scenarios.fixed;

  const pointsComparison = compareValues(
    pinnedFixed?.points || 0,
    currentFixed?.points || 0
  );
  
  const paymentComparison = compareValues(
    pinnedFixed?.paymentSchedule[0]?.monthlyPayment || 0,
    currentFixed?.paymentSchedule[0]?.monthlyPayment || 0
  );
  
  const cashComparison = compareValues(
    pinnedFixed?.cashToClose || 0,
    currentFixed?.cashToClose || 0
  );

  return (
    <div className="space-y-3 mb-4">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="text-xs font-semibold">
          📌 Comparison Mode
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearComparison}
          className="h-7 text-xs"
        >
          <X className="w-3 h-3 mr-1" />
          Clear
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Pinned Card */}
        <Card className="border-primary">
          <CardHeader className="pb-2 pt-3 px-3">
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium">PINNED</p>
              <h4 className="text-xs font-bold leading-tight">{pinnedScenario.label}</h4>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2 text-xs">
            <div>
              <p className="text-muted-foreground text-[10px]">Points</p>
              <p className="font-mono font-semibold">{formatPoints(pinnedFixed?.points)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-[10px]">Year 1 Payment</p>
              <p className="font-bold">{formatCurrency(pinnedFixed?.paymentSchedule[0]?.monthlyPayment || 0)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-[10px]">Before Credit</p>
              <p className="font-mono text-muted-foreground text-[11px]">{formatCurrency(pinnedFixed?.cashToCloseBeforeCredit || 0)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-[10px]">Cash to Close</p>
              <p className="font-semibold">{formatCurrency(pinnedFixed?.cashToClose || 0)}</p>
            </div>
          </CardContent>
        </Card>

        {/* Current Card */}
        <Card className="border-2">
          <CardHeader className="pb-2 pt-3 px-3">
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium">COMPARING</p>
              <h4 className="text-xs font-bold leading-tight">{currentScenario.label}</h4>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2 text-xs">
            <div>
              <p className="text-muted-foreground text-[10px]">Points</p>
              <div className="flex items-center gap-1">
                <p className="font-mono font-semibold">{formatPoints(currentFixed?.points)}</p>
                <pointsComparison.icon className={`w-3 h-3 ${pointsComparison.color}`} />
              </div>
              {pointsComparison.value !== 0 && (
                <p className={`text-[10px] ${pointsComparison.color}`}>
                  {pointsComparison.text}
                </p>
              )}
            </div>
            <div>
              <p className="text-muted-foreground text-[10px]">Year 1 Payment</p>
              <div className="flex items-center gap-1">
                <p className="font-bold">{formatCurrency(currentFixed?.paymentSchedule[0]?.monthlyPayment || 0)}</p>
                <paymentComparison.icon className={`w-3 h-3 ${paymentComparison.color}`} />
              </div>
              {paymentComparison.value !== 0 && (
                <p className={`text-[10px] ${paymentComparison.color}`}>
                  {paymentComparison.text}
                </p>
              )}
            </div>
            <div>
              <p className="text-muted-foreground text-[10px]">Before Credit</p>
              <p className="font-mono text-muted-foreground text-[11px]">{formatCurrency(currentFixed?.cashToCloseBeforeCredit || 0)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-[10px]">Cash to Close</p>
              <div className="flex items-center gap-1">
                <p className="font-semibold">{formatCurrency(currentFixed?.cashToClose || 0)}</p>
                <cashComparison.icon className={`w-3 h-3 ${cashComparison.color}`} />
              </div>
              {cashComparison.value !== 0 && (
                <p className={`text-[10px] ${cashComparison.color}`}>
                  {cashComparison.text}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
